import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import LeftSection from '../leftSection/leftSection';
import TopSection from '../topSection/TopSection';
import './BasicCli.css';
import '../../theme/theme.css';
import { useAppTheme } from '../../theme/AppThemeContext';
interface Props {
  children?: React.ReactNode;
}

type DrawerStage = 0 | 1 | 2;
type DockPlacement = 'left' | 'top';

const SIDEBAR_W = 292;
const SIDEBAR_GAP = 8;
const STAGE_KEY = 'app_drawer_stage_v1';
const TOGGLE_POS_KEY = 'bc_toggle_pos_v1';
const DOCK_PLACEMENT_KEY = 'app_dock_placement_v1';
const TOP_DOCK_MIN_WIDTH = 1025;
const OVERLAY_DRAWER_MAX_WIDTH = 1024;
type Pos = { x: number; y: number };
const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));
const isStage = (v: string | null): v is `${DrawerStage}` => v === '0' || v === '1' || v === '2';
const isDockPlacement = (v: string | null): v is DockPlacement => v === 'left' || v === 'top';

const BasicCli: FC<Props> = ({ children }) => {
  const location = useLocation();
  const { theme } = useAppTheme();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [isOverlayDrawer, setIsOverlayDrawer] = useState(() => window.innerWidth <= OVERLAY_DRAWER_MAX_WIDTH);
  const [isCompactLayout, setIsCompactLayout] = useState(() => window.innerWidth < TOP_DOCK_MIN_WIDTH);
  const [stage, setStage] = useState<DrawerStage>(() => {
    const saved = localStorage.getItem(STAGE_KEY);
    return isStage(saved) ? (Number(saved) as DrawerStage) : 0;
  });
  const [dockPlacement, setDockPlacement] = useState<DockPlacement>(() => {
    const saved = localStorage.getItem(DOCK_PLACEMENT_KEY);
    return isDockPlacement(saved) ? saved : 'left';
  });
  const [topInset, setTopInset] = useState(0);
  const effectiveDockPlacement: DockPlacement = isCompactLayout ? 'left' : dockPlacement;
  const topDockRef = useRef<HTMLElement | null>(null);
  const cycleStage = () =>
    setStage((s) => {
      if (isOverlayDrawer) {
        return (s === 2 ? 0 : 2) as DrawerStage;
      }

      if (effectiveDockPlacement === 'top') {
        return (s === 2 ? 0 : 2) as DrawerStage;
      }

      return ((s + 1) % 3) as DrawerStage;
    });
  const toggleSidebarMenu = () => setStage((s) => (s === 2 ? 0 : 2));
  const closeMenu = () => {
    localStorage.setItem(STAGE_KEY, '0');
    setStage(0);
  };
  const moveMenuToDock = (placement: DockPlacement) => {
    const nextPlacement = isCompactLayout ? 'left' : placement;
    setDockPlacement(nextPlacement);
    setStage(nextPlacement === 'top' ? 2 : 1);
  };
  const previousPathnameRef = React.useRef(location.pathname);
  const [togglePos, setTogglePos] = useState<Pos>(() => {
    try {
      const raw = localStorage.getItem(TOGGLE_POS_KEY);
      if (!raw) return { x: 30, y: 20 };
      const p = JSON.parse(raw) as Pos;
      return typeof p?.x === 'number' && typeof p?.y === 'number' ? p : { x: 30, y: 20 };
    } catch {
      return { x: 30, y: 20 };
    }
  });
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      setIsOverlayDrawer(window.innerWidth <= OVERLAY_DRAWER_MAX_WIDTH);
      setIsCompactLayout(window.innerWidth < TOP_DOCK_MIN_WIDTH);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  useEffect(() => {
    localStorage.setItem(TOGGLE_POS_KEY, JSON.stringify(togglePos));
  }, [togglePos]);
  useEffect(() => {
    localStorage.setItem(STAGE_KEY, String(stage === 1 ? 0 : stage));
  }, [stage]);
  useEffect(() => {
    localStorage.setItem(DOCK_PLACEMENT_KEY, dockPlacement);
  }, [dockPlacement]);
  useEffect(() => {
    if (effectiveDockPlacement === 'top' && stage === 1) {
      setStage(2);
    }
  }, [effectiveDockPlacement, stage]);
  useEffect(() => {
    if (isCompactLayout && dockPlacement !== 'left') {
      setDockPlacement('left');
    }
  }, [dockPlacement, isCompactLayout]);
  useEffect(() => {
    if (isOverlayDrawer && stage === 1) {
      setStage(0);
    }
  }, [isOverlayDrawer, stage]);
  const leftInset = !isOverlayDrawer && effectiveDockPlacement === 'left'
    ? stage === 2
      ? SIDEBAR_W + SIDEBAR_GAP
      : 0
    : 0;
  const hasTopDockFlow = !isOverlayDrawer && effectiveDockPlacement === 'top' && stage > 0;
  useEffect(() => {
    const isDrawerOpen = stage === 2;
    document.body.classList.toggle('app-drawer-open', isDrawerOpen);
    document.body.classList.add(`bcTheme-${theme}`);
    document.body.style.setProperty('--app-left-inset', `${leftInset}px`);
    document.body.style.setProperty('--app-top-inset', hasTopDockFlow ? `${topInset}px` : '0px');

    return () => {
      document.body.classList.remove('app-drawer-open');
      document.body.classList.remove(`bcTheme-${theme}`);
      document.body.style.setProperty('--app-left-inset', '0px');
      document.body.style.setProperty('--app-top-inset', '0px');
    };
  }, [hasTopDockFlow, leftInset, stage, theme, topInset]);
  useEffect(() => {
    const shouldMeasureTopDock = hasTopDockFlow;

    if (!shouldMeasureTopDock) {
      setTopInset(0);
      return;
    }

    const measureTopDock = () => {
      const nextInset = topDockRef.current?.getBoundingClientRect().height ?? 0;
      setTopInset(nextInset);
    };

    measureTopDock();
    const resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measureTopDock);
    if (topDockRef.current) resizeObserver?.observe(topDockRef.current);
    window.addEventListener('resize', measureTopDock);

    return () => {
      resizeObserver?.disconnect();
      window.removeEventListener('resize', measureTopDock);
    };
  }, [hasTopDockFlow, stage]);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && closeMenu();
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);
  useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    previousPathnameRef.current = location.pathname;

    if (!isCompactLayout) return;
    if (stage !== 2) return;
    if (previousPathname === location.pathname) return;

    closeMenu();
  }, [isCompactLayout, location.pathname, stage]);
  const startDrag = (e: React.PointerEvent<HTMLButtonElement>) => {
    const el = e.currentTarget;
    el.setPointerCapture(e.pointerId);

    const startX = e.clientX;
    const startY = e.clientY;
    const { x, y } = togglePos;

    let moved = false;

    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (Math.abs(dx) + Math.abs(dy) > 4) moved = true;

      const w = el.offsetWidth || 48;
      const h = el.offsetHeight || 48;

      const maxX = window.innerWidth - w - 8;
      const maxY = window.innerHeight - h - 8;

      setTogglePos({
        x: clamp(x + dx, 8, maxX),
        y: clamp(y + dy, 8, maxY),
      });
    };

    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);

      if (moved) {
        el.dataset.dragged = '1';
        setTimeout(() => {
          delete el.dataset.dragged;
        }, 0);
      }
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };
  const shellClass = useMemo(
    () => `bcShell bcTheme-${theme} ${hasTopDockFlow ? 'bcShellTopFlow' : ''}`.trim(),
    [hasTopDockFlow, theme],
  );
  const shellStyle = useMemo(
    () =>
      ({
        '--app-left-inset': `${leftInset}px`,
        '--app-top-inset': hasTopDockFlow ? `${topInset}px` : '0px',
      }) as React.CSSProperties & Record<'--app-left-inset' | '--app-top-inset', string>,
    [hasTopDockFlow, leftInset, topInset],
  );

  return (
    <div className={shellClass} style={shellStyle}>
      {stage === 0 && (
        <button
          className="bcToggle"
          onClick={(e) => {
            if ((e.currentTarget as HTMLButtonElement).dataset.dragged) return;
            cycleStage();
          }}
          onPointerDown={startDrag}
          style={{ left: togglePos.x, top: togglePos.y }}
          aria-label="Open menu"
          type="button"
        >
          🔳
        </button>
      )}

      {stage === 1 && (
        <div
          ref={(element) => {
            if (effectiveDockPlacement === 'top') topDockRef.current = element;
          }}
          className={`bcDock ${effectiveDockPlacement === 'top' ? 'isTop' : 'isLeft'}`}
          role="navigation"
          aria-label="Icon menu"
        >
          {effectiveDockPlacement === 'top' ? (
            <TopSection
              stage={1}
              onToggleMenu={toggleSidebarMenu}
              dockPlacement={effectiveDockPlacement}
              onSetDockPlacement={moveMenuToDock}
              onNavigate={isCompactLayout ? closeMenu : undefined}
            />
          ) : (
            <LeftSection
              stage={1}
              onToggleMenu={toggleSidebarMenu}
              dockPlacement={effectiveDockPlacement}
              onSetDockPlacement={moveMenuToDock}
              onNavigate={isCompactLayout ? closeMenu : undefined}
            />
          )}
        </div>
      )}

      {stage === 2 && (
        <>
          {isOverlayDrawer && <button type="button" className="bcDrawerBackdrop" onClick={closeMenu} aria-label="Close menu" />}
          <aside
            ref={(element) => {
              if (effectiveDockPlacement === 'top') topDockRef.current = element;
            }}
            className={`bcSidebarPersistent ${effectiveDockPlacement === 'top' ? 'isTop' : 'isLeft'} ${isOverlayDrawer ? 'isOverlayDrawer' : ''}`}
            aria-label="Sidebar menu"
            style={effectiveDockPlacement === 'top' ? undefined : { width: isMobile ? '100vw' : SIDEBAR_W }}
          >
            <div className="cont-page bcSidebarSurface">
              {effectiveDockPlacement === 'top' ? (
                <TopSection
                  stage={2}
                  onToggleMenu={toggleSidebarMenu}
                  dockPlacement={effectiveDockPlacement}
                  onSetDockPlacement={moveMenuToDock}
                  onNavigate={isCompactLayout ? closeMenu : undefined}
                />
              ) : (
                <LeftSection
                  stage={2}
                  onToggleMenu={toggleSidebarMenu}
                  dockPlacement={effectiveDockPlacement}
                  onSetDockPlacement={moveMenuToDock}
                  onNavigate={isCompactLayout ? closeMenu : undefined}
                />
              )}
            </div>
          </aside>
        </>
      )}

      <main
        className={`bcMain ${leftInset > 0 ? 'bcMainShift' : ''} ${hasTopDockFlow ? 'bcMainTopFlow' : ''}`}
        style={leftInset > 0 ? { marginLeft: leftInset } : undefined}
      >
        <div className="cont-page bcMainSurface">
          <div className="bcContent">{children}</div>
        </div>
      </main>
    </div>
  );
};

export default BasicCli;
